<template>
  <div :class="$style.wrapper">
    <component
      :is="DynamicTable" 
      title="History Ordens"
      :exportButton="true"
      :headers="headers"
      :rows="filteredRows"
      :loading-data="loadingData"
      :enable-pagination="true"
      :items-per-page="12"
    >
      <template #filters>
        <template :class="$style.filters">

          <div>
            <div :class="$style.filters__title">
              Filters
            </div>
            <div :class="$style.filters__section">
              
              <div :class="$style.inputGroup">
                <label for="startDate">Start Date:</label>
                <VueDatePicker v-model="startDate" :max-date="endDate"  hide-input-icon :month-change-on-scroll="false" :enable-time-picker="false" locale="en-US" model-type="MM-dd-yyyy" auto-apply dark placeholder="Select Date" :config="{closeOnAutoApply: true}" />
              </div>
  
              <div :class="$style.inputGroup">
                <label for="endDate">End Date:</label>
                <VueDatePicker v-model="endDate" :min-date="startDate"  hide-input-icon :month-change-on-scroll="false" :enable-time-picker="false" locale="en-US" model-type="MM-dd-yyyy" auto-apply dark placeholder="Select Date" :config="{closeOnAutoApply: true}" />
              </div>
  
              <button @click="applyFilters" :class="$style.btnSearch">
                <MagnifyingGlassIcon :class="$style.btnSearchIcon" />
                Search
              </button>
            </div>
          </div>

          <div>
            <div :class="[$style.filters__title, $style.filters__titleAdvanced]">
              <BaseButton 
                text="Advanced filters" 
                :icon="showAdvanced ? ChevronDownIcon : ChevronRightIcon"
                iconPosition="right"
                @click="showAdvanced = !showAdvanced"
              />
            </div>
            <transition name="fade">
              <div v-if="showAdvanced">
                <div :class="$style.filters__section">
                  <div :class="$style.inputGroup">
                    <label for="exchange">Exchange:</label>
                    <select v-model="selectedExchange">
                      <option value="">All Exchanges</option>
                      <option v-for="exchange in filterOptions.exchanges" :key="exchange" :value="exchange">
                        {{ exchange }}
                      </option>
                    </select>
                  </div>
      
                  <div :class="$style.inputGroup">
                    <label for="type">Type:</label>
                    <select v-model="selectedType">
                      <option value="">All Types</option>
                      <option v-for="type in filterOptions.types" :key="type" :value="type">
                        {{ type }}
                      </option>
                    </select>
                  </div>
      
                  <div :class="$style.inputGroup">
                    <label for="type">Base:</label>
                    <select v-model="selectedBase">
                      <option value="">All Bases</option>
                      <option v-for="base in filterOptions.bases" :key="base" :value="base">
                        {{ base }}
                      </option>
                    </select>
                  </div>
                </div>
                <small v-if="showAdvanced">** These filters use the loaded data and don't require a new search.</small>
              </div>
            </transition>
          </div>

        </template>
      </template>
    
    </component>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, resolveDynamicComponent } from 'vue';
  import { useStore } from 'vuex';
  import BaseTable from '@components/common/BaseTable.vue';
  import MobileTable from '@components/common/MobileTable.vue';
  import historyService from '@/services/historyService';
  import { formatToUSD } from '@/helpers/currencyFormatter';
  import { formatDate } from '@/helpers/dateFormatter';
  import VueDatePicker from '@vuepic/vue-datepicker'
  import '@vuepic/vue-datepicker/dist/main.css'
  import {
    CalendarDateRangeIcon,
    ChartBarSquareIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CurrencyDollarIcon,
    MagnifyingGlassIcon,
    PresentationChartBarIcon,
    PresentationChartLineIcon
  } from '@heroicons/vue/24/outline';
import BaseButton from '@components/common/BaseButton.vue';

  const store = useStore();
  const originalRows = ref([]);
  const loadingData = ref(true);
  const selectedExchange = ref('');
  const selectedType = ref('');
  const selectedBase = ref('');
  const showAdvanced = ref(false);
  
  const isMobile = computed(() => store.getters['device/isMobile']);

  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const formatToMMDDYYYY = (date) => {
    const d = new Date(date);
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const convertToAPIDate = (dateStr) => {
    const [month, day, year] = dateStr.split('-');
    const date = new Date(`${year}-${month}-${day}`);
    return Math.floor(date.getTime() / 1000)
  };

  const startDate = ref(formatToMMDDYYYY(sevenDaysAgo));
  const endDate = ref(formatToMMDDYYYY(today));

  const filterOptions = computed(() => {
    const exchanges = new Set();
    const types = new Set();
    const bases = new Set();

    originalRows.value.forEach(row => {
      if (row.exchange) exchanges.add(row.exchange);
      if (row.type) types.add(row.type);
      if (row.symbol) bases.add(row.symbol.toUpperCase());
    });

    return {
      exchanges: [...exchanges].sort(),
      types: [...types].sort(),
      bases: [...bases].sort()
    };
  });

  const filteredRows = computed(() => {
    return originalRows.value.filter(row => {
      return (
        (!selectedExchange.value || row.exchange === selectedExchange.value) &&
        (!selectedType.value || row.type === selectedType.value) &&
        (!selectedBase.value || row.symbol.toUpperCase() === selectedBase.value)
      );
    });
  });
  
  const DynamicTable = computed(() =>
    resolveDynamicComponent(isMobile.value ? MobileTable : BaseTable)
  );

  const headers = [
    { title: 'Date', icon: CalendarDateRangeIcon, key: 'date' },
    { title: 'Exchange', icon: ChartBarSquareIcon, key: 'exchange' },
    { title: 'Amount', icon: CurrencyDollarIcon, key: 'amount' },
    { title: 'Type', icon: PresentationChartLineIcon, key: 'type' },
    { title: 'Base', icon: PresentationChartBarIcon, key: 'symbol'}
  ];

  const applyFilters = async () => {
    await fetchData();
  };
  
  const formatData = async (data) => {
    const cleanedRows = data.filter(item => item.exchange !== 'n');

    const formattedData = cleanedRows.map(item => ({
      date: formatDate(item.date),
      exchange: item.exchange,
      amount: formatToUSD(item.amount),
      type: item.type,
      symbol: item.symbol.toUpperCase()
    }));

    return formattedData;
  };

  const fetchData = async () => {
    try {
      loadingData.value = true
      const formattedStart = convertToAPIDate(startDate.value);
      const formattedEnd = convertToAPIDate(endDate.value);
      const tableData = await historyService.getTableData(formattedStart, formattedEnd);
      originalRows.value = await formatData(tableData);
    } catch (error) {
      originalRows.value = [];
    } finally {
      loadingData.value = false;
    }
  };

  onMounted(async () => {
    await fetchData();
  });
</script>

<style module lang="scss">
  .wrapper {
    gap: 25px;
    display: flex;
    flex-wrap: wrap;
    margin-top: 20px;
    flex-direction: column;
  }

  .filters {
    gap: 10px;
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 15px;
    align-items: flex-end;
    flex-direction: column;

    &__title {
      color: #D9D9D9;
      font-size: 16px;
      font-weight: 600;
      line-height: 36px;

      &Advanced {
        display: flex;
        margin: 10px 0;
        justify-content: flex-end;
      }
    }

    &__section {
      gap: 12px;
      display: flex;
    }

    @include respond-to(null, 'sm') {
      align-items: unset;

      &__section {
        flex-wrap: wrap;
      }
    }

    @include respond-to(null, 'xs') {
      &__section {
        flex-direction: column;

        .inputGroup {
            select,
            input {
              width: 100%;
            }
        }
      }
    }
  }

  .inputGroup {
    display: flex;
    font-size: 14px;
    flex-direction: column;

    label {
      color: #d9d9d9;
      margin-bottom: 4px;
    }

    select,
    input {
      padding: 8px;
      width: 150px;
      height: 45px;
      color: #fff;
      border-radius: 6px;
      border: 1px solid #535353;
      background: rgba(217, 217, 217, 0.05);
    }

    select {
      option {
        color: black;
      }
    }
  }

  .btnSearch {
    gap: 10px;
    width: 150px;
    height: 45px;
    display: flex;
    color: #C1C1C1;
    cursor: pointer;
    font-size: 14px;
    margin-top: 21px;
    font-weight: 400;
    line-height: 10px;
    border-radius: 8px;
    font-style: normal;
    padding: 10px 21px;
    align-items: center;
    justify-content: center;
    font-family: $font-secondary;
    background: rgba(124, 249, 92, 0.15);
    border: 1px solid rgba(124, 249, 92, 0.25);

    @include respond-to(null, 'xs') {
      width: 100%;
      justify-content: center;
      line-height: 18px !important;
    }

    &Icon {
      width: 20px;
      color: #BBF90A;
    }

    &:hover {
      background: #BBF90A;
      color: #232323;

      .btnSearchIcon {
        color: #232323;
      }
    }
  }
</style>